/**
 * Translation helpers for database-stored content (products, categories).
 *
 * Convention:
 *  - Swedish is stored directly in the entity columns (name, short_description, …).
 *  - Other languages are stored in a `translations` JSONB column:
 *      { "en": { "name": "...", "short_description": "..." }, "fi": { … } }
 *  - If the requested language is "sv" or the translation is missing/empty,
 *    the Swedish default is returned.
 */

export type TranslationFields = Record<string, string | undefined>;
export type TranslationsMap = Record<string, TranslationFields>;

/**
 * Get a translated field value for a translatable entity.
 * Falls back to the Swedish default stored directly on the entity.
 */
export function getTranslatedField<T extends Record<string, any>>(
  item: T,
  field: string,
  lang: string,
): string {
  // Swedish is the default language stored in the main columns
  if (lang === 'sv') {
    return (item[field] as string) ?? '';
  }

  const translations = item.translations as TranslationsMap | null | undefined;
  const translated = translations?.[lang]?.[field];
  if (translated && translated.trim().length > 0) {
    return translated;
  }

  // Fallback to Swedish default
  return (item[field] as string) ?? '';
}
